import { useEffect, useState } from 'react';
import { Alert, AlertTitle, Box, Typography, Paper, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { checkBrowserCompatibility, getBrowserInfo } from '@/utils/browserCompatibility';

interface Props {
  children: React.ReactNode;
}

export function BrowserCompatibilityCheck({ children }: Props) {
  const [compatibility, setCompatibility] = useState<ReturnType<typeof checkBrowserCompatibility> | null>(null);
  const [showWarning, setShowWarning] = useState(true);

  useEffect(() => {
    const result = checkBrowserCompatibility();
    setCompatibility(result);
    
    // Show warning if not compatible or if on tablet/mobile with potential issues
    if (!result.isCompatible || (result.isTablet && result.missingFeatures.length > 0)) {
      setShowWarning(true);
      console.error('Browser compatibility issues detected:', result);
    }
    
    // Log browser info for debugging
    const browserInfo = getBrowserInfo();
    console.log('Browser info:', {
      ...browserInfo,
      isTablet: result.isTablet,
      isMobile: result.isMobile,
      userAgent: result.userAgent
    });
  }, []);

  if (!compatibility) {
    return null;
  }

  if (showWarning && !compatibility.isCompatible) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          p: 2,
          bgcolor: 'background.default',
        }}
      >
        <Paper
          elevation={3}
          sx={{
            p: 4,
            maxWidth: 600,
            width: '100%',
          }}
        >
          <Alert severity="error" icon={<ErrorOutlineIcon />}>
            <AlertTitle>Browser Compatibility Issue</AlertTitle>
            Your browser is missing required features for this application.
          </Alert>
          
          <Typography variant="body1" sx={{ mt: 3, mb: 2 }}>
            Missing features:
          </Typography>
          
          <List dense>
            {compatibility.missingFeatures.map((feature) => (
              <ListItem key={feature}>
                <ListItemIcon>
                  <WarningAmberIcon color="error" />
                </ListItemIcon>
                <ListItemText primary={feature} />
              </ListItem>
            ))}
          </List>
          
          <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
            Please update your browser or try a different one. This application works best with the latest versions of Chrome, Safari, Firefox, or Edge.
          </Typography>
          
          {compatibility.isTablet && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Note: You appear to be using a tablet. Make sure your browser is up to date and JavaScript is enabled.
            </Typography>
          )}
        </Paper>
      </Box>
    );
  }

  // Show a warning banner for tablets even if technically compatible
  if (compatibility.isTablet && compatibility.isCompatible && showWarning) {
    return (
      <>
        <Box sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1300 }}>
          <Alert 
            severity="info" 
            onClose={() => setShowWarning(false)}
            sx={{ 
              m: 2,
              mb: { xs: 2, sm: 3 },
              boxShadow: 3
            }}
          >
            You're using a tablet. If you experience any issues, try rotating your device or using a desktop browser.
          </Alert>
        </Box>
        {children}
      </>
    );
  }

  return <>{children}</>;
}